import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api/client';
import Modal from '../../components/common/Modal';
import Spinner from '../../components/common/Spinner';
import { FaPlus, FaEdit, FaTrash, FaLink, FaCheck, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function Subjects() {
  const [subjects,  setSubjects]  = useState([]);
  const [grades,    setGrades]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(false);
  const [linkModal, setLinkModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [selected,  setSelected]  = useState(null); // subject being linked
  const [gradeLinks, setGradeLinks] = useState([]); // current grade_ids linked to selected subject
  const [form, setForm] = useState({ subject_name: '', subject_code: '' });

  const load = () => {
    Promise.all([adminApi.getSubjectsWithGrades(), adminApi.getGrades()])
      .then(([s, g]) => { setSubjects(s.data.data); setGrades(g.data.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ subject_name: '', subject_code: '' });
    setModal(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ subject_name: s.subject_name, subject_code: s.subject_code || '' });
    setModal(true);
  };

  const openLink = async (s) => {
    setSelected(s);
    // Load which grades this subject is currently linked to
    const res = await adminApi.getSubjectsWithGrades();
    const subj = res.data.data.find(x => x.subject_id === s.subject_id);
    // Parse grade numbers back to grade_ids
    const linkedNames = subj?.grades ? subj.grades.split(', ') : [];
    const linkedIds = grades.filter(g => linkedNames.includes(g.grade_number)).map(g => g.grade_id);
    setGradeLinks(linkedIds);
    setLinkModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) await adminApi.updateSubject({ ...form, subject_id: editing.subject_id });
      else         await adminApi.addSubject(form);
      toast.success(editing ? 'Subject updated' : 'Subject added');
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const toggleGradeLink = async (gradeId) => {
    const isLinked = gradeLinks.includes(gradeId);
    try {
      if (isLinked) {
        await adminApi.unlinkSubject({ subject_id: selected.subject_id, grade_id: gradeId });
        setGradeLinks(prev => prev.filter(id => id !== gradeId));
        toast.success(`Unlinked from Grade ${grades.find(g=>g.grade_id===gradeId)?.grade_number}`);
      } else {
        await adminApi.linkSubject({ subject_id: selected.subject_id, grade_id: gradeId });
        setGradeLinks(prev => [...prev, gradeId]);
        toast.success(`Linked to Grade ${grades.find(g=>g.grade_id===gradeId)?.grade_number}`);
      }
      load(); // refresh grade count
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h2>Subjects</h2>
          <p>Manage your school's subject list, then link each subject to the grades that offer it</p>
        </div>
        <button className="sms-btn sms-btn-primary" onClick={openAdd}><FaPlus size={13}/> Add Subject</button>
      </div>

      {/* How it works hint */}
      <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10, padding:'12px 16px', marginBottom:20, display:'flex', gap:12, alignItems:'flex-start' }}>
        <span style={{ fontSize:'1.2rem', flexShrink:0 }}>💡</span>
        <div style={{ fontSize:'0.85rem', color:'#1e40af', lineHeight:1.6 }}>
          <strong>How this works:</strong> Add each subject once (e.g. Mathematics, English, Life Orientation). 
          Then use the <strong>Link to Grades</strong> button to assign it to the grades that offer it. 
          The same subject can appear in multiple grades — each grade's teacher teaches their own content.
        </div>
      </div>

      <div className="sms-card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <Spinner /> : (
          <table className="sms-table">
            <thead>
              <tr>
                <th>Subject Name</th>
                <th>Code</th>
                <th>Linked Grades</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subjects.map(s => (
                <tr key={s.subject_id}>
                  <td style={{ fontWeight:600 }}>{s.subject_name}</td>
                  <td style={{ color:'#6b7280', fontFamily:'monospace' }}>{s.subject_code || '—'}</td>
                  <td>
                    {s.grades
                      ? s.grades.split(', ').map(g => (
                          <span key={g} style={{ display:'inline-block', background:'#eff6ff', color:'#1d4ed8', fontSize:'0.75rem', fontWeight:600, padding:'2px 8px', borderRadius:12, marginRight:4, marginBottom:2 }}>
                            Gr {g}
                          </span>
                        ))
                      : <span style={{ color:'#9ca3af', fontSize:'0.82rem' }}>No grades linked</span>
                    }
                  </td>
                  <td>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="sms-btn sms-btn-outline sms-btn-sm" onClick={() => openLink(s)}
                        title="Link to grades" style={{ color:'#0d6efd', borderColor:'#bfdbfe' }}>
                        <FaLink size={11}/> Link Grades
                      </button>
                      <button className="sms-btn sms-btn-outline sms-btn-sm" onClick={() => openEdit(s)}>
                        <FaEdit size={11}/>
                      </button>
                      <button className="sms-btn sms-btn-danger sms-btn-sm"
                        onClick={async () => { if (window.confirm('Delete this subject? It will be unlinked from all grades.')) { await adminApi.deleteSubject(s.subject_id); toast.success('Deleted'); load(); }}}>
                        <FaTrash size={11}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {subjects.length === 0 && (
                <tr><td colSpan="4" style={{ textAlign:'center', color:'#9ca3af', padding:'60px 0' }}>
                  No subjects yet — add your first subject above
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit subject modal */}
      <Modal show={modal} onClose={() => setModal(false)} title={editing ? 'Edit Subject' : 'Add Subject'} size="sm">
        <form onSubmit={handleSave}>
          <div style={{ marginBottom:14 }}>
            <label className="sms-label">Subject Name <span style={{ color:'#ef4444' }}>*</span></label>
            <input className="sms-input" value={form.subject_name} onChange={e => setForm({ ...form, subject_name: e.target.value })} required placeholder="e.g. Mathematics" />
          </div>
          <div style={{ marginBottom:20 }}>
            <label className="sms-label">Subject Code</label>
            <input className="sms-input" value={form.subject_code} onChange={e => setForm({ ...form, subject_code: e.target.value })} placeholder="e.g. MATH" />
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button type="submit" className="sms-btn sms-btn-primary">Save</button>
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setModal(false)}>Cancel</button>
          </div>
        </form>
      </Modal>

      {/* Link to grades modal */}
      <Modal show={linkModal} onClose={() => { setLinkModal(false); }} title={`Link "${selected?.subject_name}" to Grades`} size="sm">
        <p style={{ color:'#6b7280', fontSize:'0.85rem', marginBottom:16, lineHeight:1.6 }}>
          Select which grades offer <strong>{selected?.subject_name}</strong>. 
          Each grade can have a different teacher and different content — the subject name stays the same.
        </p>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {grades.map(g => {
            const linked = gradeLinks.includes(g.grade_id);
            return (
              <button key={g.grade_id} onClick={() => toggleGradeLink(g.grade_id)}
                style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'12px 16px', borderRadius:10, cursor:'pointer',
                  background: linked ? '#eff6ff' : '#f9fafb',
                  border: `2px solid ${linked ? '#3b82f6' : '#e5e7eb'}`,
                  transition:'all 0.15s'
                }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:32, height:32, borderRadius:8, background: linked ? '#3b82f6' : '#e5e7eb', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <span style={{ color:'#fff', fontWeight:700, fontSize:'0.8rem' }}>{g.grade_number}</span>
                  </div>
                  <div style={{ textAlign:'left' }}>
                    <div style={{ fontWeight:600, color:'#374151' }}>Grade {g.grade_number}</div>
                    {g.grade_name && <div style={{ fontSize:'0.75rem', color:'#9ca3af' }}>{g.grade_name}</div>}
                  </div>
                </div>
                {linked
                  ? <span style={{ color:'#3b82f6', display:'flex', alignItems:'center', gap:5, fontSize:'0.82rem', fontWeight:600 }}><FaCheck size={12}/> Linked</span>
                  : <span style={{ color:'#9ca3af', fontSize:'0.82rem' }}>Click to link</span>
                }
              </button>
            );
          })}
        </div>
        <button className="sms-btn sms-btn-primary" style={{ marginTop:20, width:'100%', justifyContent:'center' }} onClick={() => { setLinkModal(false); }}>
          Done
        </button>
      </Modal>
    </div>
  );
}
